export type AgentExecutionMode = 'visual_edit' | 'structural_edit' | 'scenario_planning' | 'analysis';

export interface AgentExecutionPolicy {
  mode: AgentExecutionMode;
  objective: string;
  askBudget: number;
  defaultStrategy: string;
  refinementStrategy: string;
}

const VISUAL_PATTERNS = [
  /\bcolor\b/i,
  /\bhighlight\b/i,
  /\bbadge\b/i,
  /\blabel\b/i,
  /\bstyle\b/i,
  /\bshape\b/i,
  /\bdisplay\b/i,
];

const SCENARIO_PATTERNS = [
  /\bscenario\b/i,
  /\breorg/i,
  /\brestructure/i,
  /\bsuccession/i,
  /\bdownsize/i,
  /\bexpand\b/i,
  /\bbackfill/i,
];

const STRUCTURAL_PATTERNS = [
  /\bmove\b/i,
  /\breport(?:s|ing)? to\b/i,
  /\bassign\b/i,
  /\bpromot/i,
  /\bvacan/i,
  /\badd (?:person|position|manager|role)\b/i,
];

export function inferExecutionPolicy(input: string, diagramType?: string): AgentExecutionPolicy {
  const text = input.trim();

  if (SCENARIO_PATTERNS.some((pattern) => pattern.test(text))) {
    return {
      mode: 'scenario_planning',
      objective: 'Model a viable scenario quickly with clear scope and assumptions.',
      askBudget: 3,
      defaultStrategy: 'Default to narrow scope first, then expand if user asks.',
      refinementStrategy: 'Use compact option sets to collect only required scenario decisions.',
    };
  }

  const atlasChange = diagramType === 'org' && /^(?:please\s+)?(?:remove|delete|add|create)\s+/i.test(text);
  if (atlasChange || STRUCTURAL_PATTERNS.some((pattern) => pattern.test(text))) {
    return {
      mode: 'structural_edit',
      objective: 'Perform the requested structural change safely with minimal friction.',
      askBudget: 2,
      defaultStrategy: 'Apply conservative defaults when parameters are not high risk.',
      refinementStrategy: 'Ask only for missing execution-critical parameters.',
    };
  }

  if (VISUAL_PATTERNS.some((pattern) => pattern.test(text))) {
    return {
      mode: 'visual_edit',
      objective: 'Show immediate visible progress on canvas with minimal questions.',
      askBudget: 1,
      defaultStrategy: 'Resolve target, apply narrow scope, execute immediately.',
      refinementStrategy: 'Offer refinement chips after the first visible change.',
    };
  }

  return {
    mode: 'analysis',
    objective: 'Answer clearly and guide to the next actionable step.',
    askBudget: 2,
    defaultStrategy: 'Prefer concise answers and practical next actions.',
    refinementStrategy: 'Use short suggestion sets to narrow intent when needed.',
  };
}

export function buildClarifyingScopeText(policy: AgentExecutionPolicy): string {
  return `Mode=${policy.mode}; Objective=${policy.objective}; AskBudget=${policy.askBudget}; Defaults=${policy.defaultStrategy}; Refinement=${policy.refinementStrategy}`;
}
